/**
 * Version pin resolver.
 *
 * Resolves a version pin expression (^1.4, ~1.4, 1.4.x, latest, exact:1.4.2)
 * against a set of available versions. Ties broken by highest semver.
 *
 * @docLink packages/library/api-reference#pin-resolver
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A version pin expression string.
 *
 * Supported formats: `exact:X.Y.Z`, `X.Y.Z` (bare), `^X.Y`, `~X.Y`, `X.Y.x`, `latest`.
 *
 * @docLink packages/library/api-reference#pin-resolver
 */
export type PinExpression = string;

/**
 * Result of a successful {@link resolvePin} call.
 *
 * @docLink packages/library/api-reference#pin-resolver
 */
export interface ResolveResult {
  /** The matched version string. */
  version: string;
  /** How the match was made. */
  match: "exact" | "range" | "latest";
}

interface SemVer {
  major: number;
  minor: number;
  patch: number;
  raw: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve a version pin expression against available versions.
 *
 * Supported pin formats:
 *   - `exact:1.4.2`      — exact match only
 *   - `1.4.2`            — exact match (bare version)
 *   - `^1.4` or `^1.4.0` — caret range (>=1.4.0, <2.0.0)
 *   - `~1.4` or `~1.4.0` — tilde range (>=1.4.0, <1.5.0)
 *   - `1.4.x`            — same as ~1.4 (patch-level)
 *   - `latest`           — highest available version
 *
 * @param pin - The pin expression string.
 * @param availableVersions - All available version strings to match against.
 * @returns The best matching version (highest within range), or null if no match.
 * @docLink packages/library/api-reference#pin-resolver
 */
export function resolvePin(pin: PinExpression, availableVersions: string[]): ResolveResult | null {
  if (availableVersions.length === 0) return null;

  const parsed = availableVersions
    .map(parseSemVer)
    .filter((v): v is SemVer => v !== null)
    .sort((a, b) => compareSemVer(b, a)); // descending — highest first

  if (parsed.length === 0) return null;

  // latest — pick the highest
  if (pin === "latest") {
    return { version: parsed[0].raw, match: "latest" };
  }

  // exact:X.Y.Z — strict equality
  if (pin.startsWith("exact:")) {
    return resolveExactPin(pin, parsed);
  }

  // ^X.Y.Z or ^X.Y — caret range (>=X.Y.Z, <(X+1).0.0)
  if (pin.startsWith("^")) {
    return resolveCaretPin(pin, parsed);
  }

  // ~X.Y.Z or ~X.Y — tilde range (>=X.Y.Z, <X.(Y+1).0)
  if (pin.startsWith("~")) {
    return resolveTildePin(pin, parsed);
  }

  // X.Y.x — same as ~X.Y.0; X.x (major-only) matches the whole major.
  if (pin.endsWith(".x")) {
    return resolveWildcardPin(pin, parsed);
  }

  // Bare version — exact match
  return resolveBarePin(pin, parsed);
}

/** Resolve `exact:X.Y.Z` — strict equality. `parsed` is sorted descending. */
function resolveExactPin(pin: string, parsed: SemVer[]): ResolveResult | null {
  const target = pin.slice(6);
  const found = parsed.find((v) => v.raw === target);
  return found ? { version: found.raw, match: "exact" } : null;
}

/**
 * Resolve `^X.Y.Z` / `^X.Y` — caret range (>=X.Y.Z, <(X+1).0.0). `parsed` is
 * sorted descending, so the first match is the highest in range.
 *
 * NOTE: Deviates from npm semver for major-zero ranges. npm treats ^0.1.4 as
 * >=0.1.4, <0.2.0 (minor-locked). We treat it as >=0.1.4, <1.0.0 (major-locked).
 * This is acceptable in Phase 1 (no assets use 0.x versions). Fix before Phase 2.
 */
function resolveCaretPin(pin: string, parsed: SemVer[]): ResolveResult | null {
  const target = parseRangeTarget(pin.slice(1));
  if (!target) return null;

  const match = parsed.find(
    (v) =>
      v.major === target.major &&
      (v.minor > target.minor || (v.minor === target.minor && v.patch >= target.patch)),
  );
  return match ? { version: match.raw, match: "range" } : null;
}

/**
 * Resolve `~X.Y.Z` / `~X.Y` — tilde range (>=X.Y.Z, <X.(Y+1).0). `parsed` is
 * sorted descending, so the first match is the highest in range.
 */
function resolveTildePin(pin: string, parsed: SemVer[]): ResolveResult | null {
  const target = parseRangeTarget(pin.slice(1));
  if (!target) return null;

  const match = parsed.find(
    (v) => v.major === target.major && v.minor === target.minor && v.patch >= target.patch,
  );
  return match ? { version: match.raw, match: "range" } : null;
}

/**
 * Resolve `X.Y.x` — same as ~X.Y.0; `X.x` (major-only) matches the whole major.
 * `parsed` is sorted descending, so the first match is the highest in range.
 */
function resolveWildcardPin(pin: string, parsed: SemVer[]): ResolveResult | null {
  const base = pin.slice(0, -2);
  const parts = base.split(".");
  const major = Number.parseInt(parts[0], 10);
  // Major-only `X.x` has no minor component — don't pin minor to 0.
  const minor = parts.length > 1 ? Number.parseInt(parts[1], 10) : undefined;

  if (Number.isNaN(major)) return null;

  const match = parsed.find((v) => v.major === major && (minor === undefined || v.minor === minor));
  return match ? { version: match.raw, match: "range" } : null;
}

/** Resolve a bare `X.Y.Z` version — exact match. `parsed` is sorted descending. */
function resolveBarePin(pin: string, parsed: SemVer[]): ResolveResult | null {
  const bareSemver = parseSemVer(pin);
  if (bareSemver) {
    const found = parsed.find((v) => v.raw === pin);
    return found ? { version: found.raw, match: "exact" } : null;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseSemVer(v: string): SemVer | null {
  const match = v.match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!match) return null;
  return {
    major: Number.parseInt(match[1], 10),
    minor: Number.parseInt(match[2], 10),
    patch: Number.parseInt(match[3], 10),
    raw: v,
  };
}

function compareSemVer(a: SemVer, b: SemVer): number {
  if (a.major !== b.major) return a.major - b.major;
  if (a.minor !== b.minor) return a.minor - b.minor;
  return a.patch - b.patch;
}

function parseRangeTarget(range: string): { major: number; minor: number; patch: number } | null {
  const parts = range.split(".");
  const major = Number.parseInt(parts[0], 10);
  if (Number.isNaN(major)) return null;
  const minor = parts.length > 1 ? Number.parseInt(parts[1], 10) : 0;
  const patch = parts.length > 2 ? Number.parseInt(parts[2], 10) : 0;
  return {
    major,
    minor: Number.isNaN(minor) ? 0 : minor,
    patch: Number.isNaN(patch) ? 0 : patch,
  };
}
